import { useSettings } from '../SettingsProvider';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useSaveSettings } from '@ais-ui/websockets';
import React, { useEffect, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  useToast,
} from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { useSettingsPage } from '../SettingsPageProvider';

const eventsFormSchema = z.object({
  lookback_time_in_minutes: z.number(),
});

type EventsFormValues = z.infer<typeof eventsFormSchema>;

export const EventsForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { mutate: mutateSettings } = useSaveSettings();

  const initialValues = {
    lookback_time_in_minutes:
      settings['ribbonPlugin.settings']?.event_lookback_minutes || 720,
  };

  const form = useForm<EventsFormValues>({
    resolver: zodResolver(eventsFormSchema),
    defaultValues: initialValues,
  });

  const { setUnsavedSettings } = useSettingsPage();

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  function onSubmit(data: EventsFormValues) {
    setIsSubmitting(true);
    const settingsToSave = {
      ...settings,
      'ribbonPlugin.settings': {
        ...settings['ribbonPlugin.settings'],
        event_lookback_minutes: data.lookback_time_in_minutes,
      },
    };
    mutateSettings(settingsToSave, {
      onSuccess: () => {
        toast({
          className: 'update-events-enable-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.settingsEventsSuccessUpdateMessage,
        });
        setIsSubmitting(false);
        setUnsavedSettings(false);
      },
      onError: () => {
        toast({
          className: 'update-events-settings-error-toast',
          title: messages.genericToastTitleError,
          description: messages.settingsEventsFailedUpdateMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="space-y-4">
          <FormField
            key="lookback_time_in_minutes"
            control={form.control}
            name="lookback_time_in_minutes"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex flex-row items-center justify-between border rounded-lg p-4'
                )}
              >
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    {messages.settingsEventsLookbackLabel}
                  </FormLabel>
                  <FormDescription>
                    {messages.settingsEventsLookbackDescription}
                  </FormDescription>
                </div>
                <FormControl
                  id="settings-events-lookback"
                  onChange={() => form.clearErrors()}
                >
                  <Input
                    className="w-20"
                    type="number"
                    {...field}
                    onChange={(e) => {
                      if (!e.target.value) {
                        field.onChange('');
                      } else {
                        // Convert the string value to a number
                        const numberValue = parseFloat(e.target.value);
                        field.onChange(numberValue);
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="flex items-center justify-end gap-2 pr-2">
          <Button
            id="settings-events-save-button"
            onClick={form.handleSubmit(onSubmit)}
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
